<template>
  <div class="wrapper businesses-page">
    <!-- Enhanced Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-title">
          <h1 class="title">Businesses</h1>
        </div>
        
        <div class="header-actions">
          <div class="search-section">
            <div class="search-controls">
              <div class="enhanced-search-container">
                <div class="search-wrapper">
                  <span class="search-icon">
                    <i class="las la-search"></i>
                  </span>
                  <input
                    ref="searchInput"
                    type="text"
                    class="search-input"
                    placeholder="Search by business name"
                    v-model="searchTerm"
                    @input="handleSearchInput"
                    @focus="onInputFocus"
                    @blur="onInputBlur"
                  />
                  <span v-if="searchTerm" class="clear-icon" @click="clearSearch">
                    <i class="las la-times"></i>
                  </span>
                </div>
              </div>

              <div class="financing-filter">
                <i class="las la-filter filter-icon"></i>
                <select
                  class="filter-select"
                  v-model="financingFilter"
                  @change="onFinancingFilterChange"
                >
                  <option value="all">All businesses</option>
                  <option value="active">Financing active</option>
                  <option value="inactive">Financing inactive</option>
                </select>
              </div>
            </div>
            
            <div class="results-summary" v-if="totalRows > 0">
              <div class="results-badge">
                <i class="las la-building"></i>
                <span>{{ totalRows }}</span>
              </div>
              <span class="results-text">
                {{ totalRows === 1 ? 'business' : 'businesses' }} found
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <div class="content-wrapper">
        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="isEmpty"
        >
          <i class="las la-building la-5x text-muted mb-3"></i>
          <div class="empty-title">
            <span v-if="searchTerm">No businesses found for "{{ searchTerm }}"</span>
            <span v-else-if="financingFilter === 'active'">No businesses with financing active</span>
            <span v-else-if="financingFilter === 'inactive'">No businesses with financing inactive</span>
            <span v-else>No businesses found</span>
          </div>
          <div class="empty-subtitle" v-if="searchTerm || financingFilter !== 'all'">
            Try adjusting your search or financing filter
          </div>
        </div>

        <!-- Error State -->
        <div
          class="empty-state text-center"
          v-if="error"
        >
          <i class="las la-exclamation-triangle la-5x text-danger mb-3"></i>
          <div class="empty-title text-danger">
            {{ error }}
          </div>
                      <button 
              class="btn btn-primary btn-sm mt-3" 
              @click="fetchBusinesses"
            >
              <i class="las la-redo mr-1"></i>
              Try Again
            </button>
        </div>

        <!-- Enhanced Business Grid -->
        <div v-if="!isEmpty && !error" class="businesses-grid" :class="{ 'large-dataset': totalRows > 50 }">
          <!-- Grid Header -->
          <div class="grid-header">
            <div class="header-row">
              <div class="header-cell business-col">
                <i class="las la-building"></i>
                <span>Business</span>
              </div>
              <div class="header-cell admin-col">
                <i class="las la-user"></i>
                <span>Admin</span>
              </div>
              <div class="header-cell email-col">
                <i class="las la-envelope"></i>
                <span>Email</span>
              </div>
              <div class="header-cell country-col">
                <i class="las la-globe"></i>
                <span>Country</span>
              </div>
              <div class="header-cell currency-col">
                <i class="las la-coins"></i>
                <span>Currency</span>
              </div>
              <div class="header-cell status-col">
                <i class="las la-toggle-on"></i>
                <span>Status</span>
              </div>
              <div class="header-cell settings-col">
                <i class="las la-cog"></i>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Grid Body -->
          <div class="grid-body">
            <div 
              v-for="business in businesses" 
              :key="business.id"
              class="business-card"
              :class="{ 'financing-disabled': !business.financing_enabled }"
              @click="handleBusinessClick(business)"
            >
              <div class="business-cell business-col">
                <div 
                  class="business-info"
                  v-b-tooltip.hover="!business.financing_enabled ? 'Financing is not enabled for this business' : null"
                >
                  <div class="business-avatar">
                    <img 
                      v-if="business.image" 
                      :src="business.image" 
                      :alt="business.name"
                      class="avatar-img"
                      @error="handleImageError($event, business)"
                    />
                    <div v-else class="avatar-placeholder">
                      <span class="avatar-initials">{{ getBusinessInitials(business.name) }}</span>
                    </div>
                  </div>
                  <div class="business-details">
                    <div class="business-name">{{ business.name }}</div>
                  </div>
                </div>
              </div>
              
              <div class="business-cell admin-col" data-label="Admin">
                <div class="admin-info">
                  <div class="admin-name">{{ business.admin_name }}</div>
                </div>
              </div>
              
              <div class="business-cell email-col" data-label="Email">
                <div class="email-info">
                  <a :href="`mailto:${business.admin_email}`" class="email-link" @click.stop>
                    {{ business.admin_email }}
                  </a>
                </div>
              </div>
              
              <div class="business-cell country-col" data-label="Country">
                <div class="country-info">
                  <span class="country-code">{{ business.country_display }}</span>
                </div>
              </div>
              
              <div class="business-cell currency-col" data-label="Currency">
                <div class="currency-info">
                  <span class="currency-code">{{ business.base_currency }}</span>
                </div>
              </div>
              
              <div class="business-cell status-col" data-label="Status">
                <span 
                  class="status-badge"
                  :class="{ 'active': business.is_active, 'inactive': !business.is_active }"
                >
                  {{ business.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>

              <div class="business-cell settings-col" data-label="Settings">
                <div class="action-menu-wrapper">
                  <button 
                    class="action-menu-trigger" 
                    @click.stop="toggleActionMenu(business.id)"
                    :class="{ 'active': openMenuId === business.id }"
                    type="button"
                  >
                    <i class="las la-ellipsis-v"></i>
                  </button>
                  
                  <transition name="dropdown-fade">
                    <div 
                      v-if="openMenuId === business.id"
                      class="action-menu-dropdown" 
                      @click.stop
                    >
                      <div class="action-menu-item" @click="openFinancingSettings(business)">
                        <i class="las la-cog"></i>
                        <span>Settings</span>
                      </div>
                      <div class="action-menu-item" @click="openErpExtractor(business)">
                        <i class="las la-database"></i>
                        <span>ERP Extractor</span>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>

          <!-- Standard Pagination -->
          <base-pagination
            :activePage="currentPage"
            :totalRows="totalRows"
            :pageSize="pageSize"
            @set-page="setPage"
            tableName="businesses"
          />
        </div>
      </div>
    </div>

    <!-- ERP Modal -->
    <erp-modal ref="erpModal" />

  </div>
</template>

<script>
import CompaniesService from "@/services/companies.service";
import BasePagination from "@/components/UI/BasePagination.vue";
import ErpModal from "@/views/pages/companies/components/erpModal.vue";

export default {
  name: "BusinessesPage",
  components: {
    BasePagination,
    ErpModal,
  },
  data() {
    return {
      businesses: [],
      error: null,
      searchTerm: "",
      financingFilter: "active",
      debounceTimer: null,
      currentPage: 1,
      pageSize: 20,
      totalRows: 0,
      inputHasFocus: false,
      openMenuId: null,
    };
  },
  computed: {
    isEmpty() {
      return this.businesses.length === 0;
    },
  },
  mounted() {
    this.fetchBusinesses();
    // Close menu when clicking outside
    document.addEventListener('click', this.closeAllMenus);
  },
  
  beforeDestroy() {
    // Clean up timer to prevent memory leaks
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    // Remove event listener
    document.removeEventListener('click', this.closeAllMenus);
  },
  methods: {
    async fetchBusinesses(page = 1, shouldMaintainFocus = false) {
      this.error = null;
      
      try {
        const params = {
          page,
          page_size: this.pageSize,
          list_view:'True'
        };
        
        if (this.searchTerm) {
          params.search = this.searchTerm;
        }

        if (this.financingFilter === "active") {
          params.financing_enabled = true;
        } else if (this.financingFilter === "inactive") {
          params.financing_enabled = false;
        }
        
        const response = await CompaniesService.query(params);
        
        this.businesses = this.formatBusinessData(response.data || []);
        this.totalRows = response.count || 0;
        this.currentPage = page;
        
        // Restore focus after API call is complete and DOM is updated
        if (shouldMaintainFocus && this.$refs.searchInput) {
          // Use a small delay to ensure DOM is fully updated
          this.$nextTick(() => {
            setTimeout(() => {
              try {
                const inputElement = this.$refs.searchInput;
                if (inputElement) {
                  inputElement.focus();
                  // Set cursor to end of text for better UX
                  const textLength = inputElement.value.length;
                  inputElement.setSelectionRange(textLength, textLength);
                  // Update our focus tracking
                  this.inputHasFocus = true;
                }
              } catch (e) {
                // Silently handle any focus errors
                console.debug("Focus restoration failed:", e);
              }
            }, 10); // Small delay to ensure everything is rendered
          });
        }
        
      } catch (error) {
        console.error("Error fetching businesses:", error);
        this.error = "Failed to load businesses. Please try again.";
        this.businesses = [];
        this.totalRows = 0;
      }
    },
    
    formatBusinessData(businesses) {
      return businesses.map(business => ({
        id: business.id,
        name: business.name || "Unknown Business",
        admin_name: business.admin_user 
          ? `${business.admin_user.first_name} ${business.admin_user.last_name}`.trim()
          : "No Admin",
        admin_email: business.admin_user?.email || "No Email",
        country_display: business.country || "N/A",
        base_currency: business.base_currency || "N/A",
        is_active: business.active,
        financing_enabled: business.financing_enabled !== false, // Default to true if not present
        image: this.getBusinessImageUrl(business.image),
        // Raw data for potential future use
        raw: business,
      }));
    },
    
    handleSearchInput() {
      // Clear existing timer
      clearTimeout(this.debounceTimer);
      
      // Use our tracked focus state which is more reliable
      const shouldMaintainFocus = this.inputHasFocus;
      
      // Set new timer for debounced search
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchBusinesses(1, shouldMaintainFocus);
      }, 300);
    },
    
    clearSearch() {
      this.searchTerm = "";
      this.currentPage = 1;
      // Always maintain focus when clearing search since user clicked the clear button
      this.fetchBusinesses(1, true);
    },

    onFinancingFilterChange() {
      this.currentPage = 1;
      this.fetchBusinesses(1);
    },
    
    onInputFocus() {
      this.inputHasFocus = true;
    },
    
    onInputBlur() {
      this.inputHasFocus = false;
    },
    
    setPage(page, pageSize) {
      // If pageSize is provided and different from current, update it
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      
      // Fetch businesses for the requested page
      if (page >= 1) {
        this.fetchBusinesses(page);
      }
    },
    
    getBusinessImageUrl(image) {
      if (!image) return null;
      
      // Handle different image object structures
      if (typeof image === 'object') {
        // Try different sizes in order of preference
        const imageUrl = image['128'] || image['64'] || image['32'] || image.url || image;
        return imageUrl ? `${process.env.VUE_APP_API_URL}${imageUrl}` : null;
      }
      
      // Handle direct URL strings
      if (typeof image === 'string') {
        return image.startsWith('http') ? image : `${process.env.VUE_APP_API_URL}${image}`;
      }
      
      return null;
    },
    
    getBusinessInitials(name) {
      if (!name) return 'B';
      
      const words = name.trim().split(' ');
      if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
      }
      
      return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    },
    
    handleImageError(event, business) {
      // Hide the broken image and show initials instead
      event.target.style.display = 'none';
      // Find the parent avatar container and add error class
      const avatarContainer = event.target.closest('.business-avatar');
      if (avatarContainer) {
        avatarContainer.classList.add('image-error');
      }
      // Update the business object to prevent retry
      business.image = null;
    },
    openFinancingSettings(business) {
      // Close menu first
      this.closeAllMenus();
      // Navigate to the dedicated financing settings page
      this.$router.push({
        name: 'financing-settings',
        params: { business_id: business.id }
      });
    },
    
    viewBusiness(business) {
      // Navigate to business insights page
      this.$router.push({
        name: 'business-insights',
        params: { business_id: business.id }
      });
    },
    
    handleBusinessClick(business) {
      // Only allow navigation if financing is enabled
      if (business.financing_enabled) {
        this.viewBusiness(business);
      }
    },
    
    toggleActionMenu(businessId) {
      // Toggle menu for specific business
      this.openMenuId = this.openMenuId === businessId ? null : businessId;
    },
    
    closeAllMenus() {
      this.openMenuId = null;
    },
    
    openErpExtractor(business) {
      // Close menu first
      this.closeAllMenus();
      // Open ERP Modal with business data
      if (this.$refs.erpModal) {
        this.$refs.erpModal.show(business.raw || business);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import './index.scss';
</style>
